import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Linkedin, Instagram, Music, ArrowRight } from 'lucide-react';
import { Logo, Button } from '@/src/components/ui';
import {
  FOOTER_SERVICES,
  FOOTER_COMPANY,
  BRAND,
  SOCIAL_LINKS,
  CALENDLY_URL,
  GUESTLY_URL,
} from '@/src/constants';

const socialIcons = [
  { Icon: Twitter, href: SOCIAL_LINKS.twitter, label: 'X (Twitter)' },
  { Icon: Linkedin, href: SOCIAL_LINKS.linkedin, label: 'LinkedIn' },
  { Icon: Music, href: SOCIAL_LINKS.tiktok, label: 'TikTok' },
  { Icon: Instagram, href: SOCIAL_LINKS.instagram, label: 'Instagram' },
];

// Mapeo de items del footer a sus rutas
const footerRoutes: Record<string, string> = {
  'Consultoría AI': '/servicios/consultoria',
  'Desarrollo de Chatbots': '/servicios/chatbot-crewai',
  Automatización: '/servicios/automatizacion',
  'Crew de Agentes': '/servicios/agents-crew',
  'Sobre Nosotros': '/empresa/sobre-nosotros',
  Blog: '/empresa/blog',
  Contacto: '/empresa/contacto',
};

function FooterLinkList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4 className="text-white font-bold mb-8 tracking-wide">{title}</h4>
      <ul className="space-y-4 text-gray-500 text-sm">
        {items.map((item, idx) => {
          const route = footerRoutes[item];
          return (
            <li key={idx}>
              <Link
                to={route || '/'}
                className="group flex items-center gap-3 transition-all duration-300 hover:text-white hover:translate-x-2"
              >
                <span className="h-[2px] w-0 bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)] transition-all duration-300 group-hover:w-4 rounded-full" />
                <span className="transition-colors duration-300">{item}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#020202]">
      {/* Pre-Footer CTA */}
      <div className="border-b border-white/5">
        <div className="container mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-3xl font-bold text-white mb-2">¿Listo para escalar?</h3>
            <p className="text-gray-400">Agenda una consultoría gratuita de 15 minutos.</p>
          </div>

          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            <Button variant="secondary" size="lg" icon={<ArrowRight size={20} />}>
              Comenzar Proyecto
            </Button>
          </a>
        </div>
      </div>

      <div className="container mx-auto px-6 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <div className="mb-6">
              <Logo size="md" />
            </div>
            <p className="text-gray-500 max-w-sm mb-8 leading-relaxed">
              Agencia de desarrollo e implementación de Inteligencia Artificial para empresas que
              miran al futuro. Transformando datos en decisiones.
            </p>

            {/* Guestly CTA */}
            <a
              href={GUESTLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl hover:from-emerald-500/30 hover:to-teal-500/30 hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <span className="text-emerald-400 text-sm font-medium">Gestiona tus huéspedes</span>
              <ArrowRight
                size={16}
                className="text-emerald-400 group-hover:translate-x-1 transition-transform"
              />
            </a>

            <div className="flex gap-4 mt-6">
              {socialIcons.map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="p-3 bg-white/5 border border-white/5 rounded-full hover:bg-white hover:text-black hover:border-white text-gray-400 transition-all duration-300 hover:scale-110 shadow-[0_0_0_rgba(255,255,255,0)] hover:shadow-[0_0_15px_rgba(255,255,255,0.4)]"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <FooterLinkList title="Servicios" items={FOOTER_SERVICES} />
          <FooterLinkList title="Empresa" items={FOOTER_COMPANY} />
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-gray-600">
          <p>
            &copy; {BRAND.year} {BRAND.name} AI Agency. Todos los derechos reservados.
          </p>
          <div className="flex gap-8 mt-4 md:mt-0">
            <Link to="/privacidad" className="hover:text-white transition-colors">
              Privacidad
            </Link>
            <Link to="/terminos" className="hover:text-white transition-colors">
              Términos
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default memo(Footer);
